import { motion } from "framer-motion";
import { MessageSquare, Lightbulb, CheckCircle2, Globe, Languages, Scale, Building2, Users, Shield, Smartphone, Zap, BarChart3 } from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import Layout from "@/components/Layout";
import SectionHeader from "@/components/SectionHeader";
import { ROUTE_PATHS } from "@/lib/index";
import { staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function Platform() {
  const howItWorksSteps = [
    {
      number: "1",
      icon: MessageSquare,
      title: "Ask a question",
      description: "Send a message in your own words, no special skills needed. Just ask like you're talking to a friend.",
    },
    {
      number: "2",
      icon: Lightbulb,
      title: "Guided response",
      description: "Get clear, step-by-step guidance tailored to your situation. No jargon, just practical help.",
    },
    {
      number: "3",
      icon: CheckCircle2,
      title: "Practical next steps",
      description: "Receive actionable advice you can use right away. Follow-up support whenever you need it.",
    },
  ];

  const localisationPillars = [
    {
      icon: Languages,
      title: "Languages",
      description: "Support for local languages and dialects across Africa",
    },
    {
      icon: Scale,
      title: "Local Laws",
      description: "Guidance aligned with country-specific legal frameworks",
    },
    {
      icon: Building2,
      title: "Local Services",
      description: "Connected to local resources, providers, and institutions",
    },
    {
      icon: Users,
      title: "Culture",
      description: "Culturally appropriate advice and communication styles",
    },
    {
      icon: Shield,
      title: "Local Institutions",
      description: "Integration with local schools, clinics, and government services",
    },
  ];

  const trustCards = [
    {
      icon: MessageSquare,
      title: "No prompting skills required",
      description: "Ask questions naturally. No need to learn special commands or techniques.",
    },
    {
      icon: Shield,
      title: "Does not guess — escalates when needed",
      description: "When uncertain, the system connects you to human support rather than guessing.",
    },
    {
      icon: CheckCircle2,
      title: "Built for real-world reliability",
      description: "Tested across diverse African contexts for consistent, trustworthy guidance.",
    },
  ];

  const techSpecs = [
    { label: "Messaging delivery", value: "WhatsApp, SMS, and more" },
    { label: "White-label API", value: "Full customisation and branding" },
    { label: "Multi-language", value: "10+ African languages" },
    { label: "Family profiles", value: "Up to 5 members per account" },
    { label: "Analytics dashboard", value: "Real-time usage and impact metrics" },
  ];

  const futureChannels = [
    { name: "SMS", icon: Smartphone },
    { name: "USSD", icon: Zap },
    { name: "Web", icon: Globe },
    { name: "App", icon: Smartphone },
  ];

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative bg-primary text-primary-foreground py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.AFRICA_TECH_16}
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/50 via-transparent to-primary/70" />
          </div>

          <motion.div
            className="container mx-auto px-4 relative z-10"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div className="max-w-4xl mx-auto text-center" variants={staggerItem}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                The platform behind everyday family support
              </h1>
              <p className="text-xl md:text-2xl text-primary-foreground/90 mb-8">
                Built for Africa. Designed for families. Ready for scale.
              </p>
            </motion.div>
          </motion.div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="How It Works"
              headline="Simple for families. Powerful for businesses."
              subheadline="Three steps to practical support, delivered through familiar messaging platforms"
              align="center"
              className="mb-16"
            />

            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
                <div className="hidden md:block absolute top-1/3 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-accent/30 to-transparent" />

                {howItWorksSteps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    className="relative"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ ...springPresets.gentle, delay: index * 0.15 }}
                  >
                    <div className="bg-card rounded-3xl p-8 shadow-lg border border-border relative z-10 h-full">
                      <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mb-6 mx-auto">
                        <step.icon className="w-10 h-10 text-accent" />
                      </div>
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-2xl font-bold shadow-lg">
                        {step.number}
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-center">{step.title}</h3>
                      <p className="text-muted-foreground text-center leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Built for Africa"
              headline="Localisation at the core"
              subheadline="Every market is different. Sammo adapts to local languages, laws, services, and culture."
              align="center"
              className="mb-16"
            />

            <div className="max-w-5xl mx-auto mb-16">
              <motion.div
                className="relative aspect-[16/9] rounded-3xl overflow-hidden shadow-2xl"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={springPresets.gentle}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-primary/10 to-accent/20" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Globe className="w-32 h-32 md:w-48 md:h-48 text-accent/40" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                      10+ Countries
                    </h3>
                    <p className="text-lg text-muted-foreground">Ready for localisation</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
              {localisationPillars.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  className="bg-card rounded-2xl p-6 shadow-md border border-border hover:shadow-xl transition-shadow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ ...springPresets.gentle, delay: index * 0.08 }}
                >
                  <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <pillar.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{pillar.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Trust & Safety"
              headline="Built for real-world reliability"
              subheadline="Families trust Sammo because it's designed to be safe, honest, and dependable"
              align="center"
              className="mb-16"
            />

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {trustCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  className="bg-card rounded-3xl p-8 shadow-lg border border-border"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                    <card.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{card.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{card.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Delivery Channels"
              headline="Starts on familiar messaging channels, built for more"
              subheadline="WhatsApp today. SMS, USSD, web, and app tomorrow."
              align="center"
              className="mb-16"
            />

            <div className="max-w-4xl mx-auto">
              <motion.div
                className="bg-card rounded-3xl p-12 shadow-xl border border-border mb-8"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={springPresets.gentle}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 rounded-3xl bg-[#25D366]/10 flex items-center justify-center mb-6">
                    <SiWhatsapp className="w-14 h-14 text-[#25D366]" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4">WhatsApp First</h3>
                  <p className="text-lg text-muted-foreground max-w-2xl">
                    Start where your customers already are. No app downloads, no new accounts. Just
                    send a message.
                  </p>
                </div>
              </motion.div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {futureChannels.map((channel, index) => (
                  <motion.div
                    key={channel.name}
                    className="bg-card rounded-2xl p-6 shadow-md border border-border text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springPresets.gentle, delay: index * 0.08 }}
                  >
                    <channel.icon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">{channel.name}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Coming soon</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <SectionHeader
              eyebrow="Technical Specifications"
              headline="Enterprise-ready platform"
              subheadline="Built for scale, security, and seamless integration"
              align="center"
              className="mb-16"
            />

            <motion.div
              className="max-w-4xl mx-auto bg-card rounded-3xl shadow-xl border border-border overflow-hidden"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <div className="divide-y divide-border">
                {techSpecs.map((spec, index) => (
                  <motion.div
                    key={spec.label}
                    className="flex items-center justify-between p-6 md:p-8 hover:bg-muted/30 transition-colors"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springPresets.gentle, delay: index * 0.05 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-5 h-5 text-accent" />
                      </div>
                      <span className="font-semibold text-lg">{spec.label}</span>
                    </div>
                    <span className="text-muted-foreground text-right">{spec.value}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-b from-muted/30 to-background">
          <motion.div
            className="container mx-auto px-4 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
              variants={staggerItem}
            >
              Ready to see Sammo in action?
            </motion.h2>
            <motion.p
              className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
              variants={staggerItem}
            >
              Discover how Sammo can transform customer relationships for your business
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" variants={staggerItem}>
              <Button asChild size="lg" className="text-lg px-8">
                <Link to={ROUTE_PATHS.FOR_BUSINESS}>See Sammo for Your Business</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <Link to={ROUTE_PATHS.CONTACT}>Book a Demo</Link>
              </Button>
            </motion.div>
          </motion.div>
        </section>
      </div>
    </Layout>
  );
}